import { Assignment } from '@mui/icons-material'
import {
  ActivityType,
  ACTIVITY_COLORS,
  ACTIVITY_ICONS,
} from '../types/activity'
import type { ActivityItem } from '../types/activity'

export const getActivityColor = (type: string) => {
  return ACTIVITY_COLORS[type as ActivityType] || '#9e9e9e'
}

export const getActivityIcon = (type: string) => {
  const IconComponent = ACTIVITY_ICONS[type as ActivityType] || Assignment
  return <IconComponent fontSize="small" sx={{ color: 'white' }} />
}

export const formatActivityType = (type: string) => {
  return type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
}

export function formatTimestampNZ(timestamp: string) {
  const value = timestamp + (timestamp.endsWith('Z') ? '' : 'Z')
  return new Date(value).toLocaleString('en-NZ', {
    timeZone: 'Pacific/Auckland',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

export function extractMembershipStatuses(activityDetails: string) {
  const match = activityDetails.match(
    /Membership changed from (.+?) to (.+?) \(([^)]+)\)/
  )
  if (match) {
    const fromStatus = match[1].trim()
    const toStatus = match[2].trim()
    const paymentType = match[3].trim()

    return {
      from: fromStatus,
      to: toStatus,
      paymentType: paymentType,
    }
  }
  return { from: '', to: '', paymentType: '' }
}

export function getFullStatusName(status: string, paymentType: string): string {
  if (status === 'Paid' || status === 'Cancelled') {
    return `${status} (${paymentType})`
  }
  return status
}

export function buildMembershipSequence(membershipChanges: ActivityItem[]) {
  const sequence: ActivityItem[] = []

  const remaining = [...membershipChanges]

  // start from the end of the chain (most recent event)
  let current = remaining.find((activity) => {
    const currentStatus = extractMembershipStatuses(activity.activity_details)
    const currentToFull = getFullStatusName(
      currentStatus.to,
      currentStatus.paymentType
    )

    // find activity that no other activity is following
    return !remaining.some((other) => {
      if (other === activity) return false
      const otherStatus = extractMembershipStatuses(other.activity_details)
      const otherFromFull = getFullStatusName(
        otherStatus.from,
        otherStatus.paymentType
      )
      return otherFromFull.toLowerCase() === currentToFull.toLowerCase()
    })
  })

  if (!current) {
    // use timestamp order if we can't build logical chain
    return membershipChanges.sort(
      (a, b) => new Date(a.date || a.activity_timestamp).getTime() - new Date(b.date || b.activity_timestamp).getTime()
    )
  }

  // build sequence following the chain of membership changes (recent first)
  while (current && remaining.length > 0) {
    sequence.push(current)
    const currentIndex = remaining.indexOf(current)

    if (currentIndex > -1) {
      remaining.splice(currentIndex, 1)
    }

    if (remaining.length === 0) break

    // find prev activity
    const currentStatuses = extractMembershipStatuses(current.activity_details)

    current = remaining.find((activity) => {
      const activityStatuses = extractMembershipStatuses(
        activity.activity_details
      )

      // match just the status name, not the payment type
      return (
        activityStatuses.to.toLowerCase() === currentStatuses.from.toLowerCase()
      )
    })
  }
  sequence.push(...remaining)

  return sequence
}

export function normalizeActivityItem(item: unknown): ActivityItem {
  const typedItem = item as Record<string, any>
  const ts = typedItem.date || typedItem.activity_timestamp
  return {
    ...typedItem,
    activity_type: typedItem.activity_type,
    activity_timestamp: ts,
    date: ts,
  } as ActivityItem
}
